import ApiService from '@/services/ApiService';
import {
  DashboardCardUnit,
  DashboardCardData,
  DashboardCardType,
  DashboardDataNormalized,
  DashboardQuery,
} from '@/models/dashboard';
import { DashboardResponse } from '@/models';
import UtilsService from './UtilsService';
import { NormalizeOptions } from '../models/dashboard';
import { Feature } from '../models';
import { OmniChatSchema } from '@omnichat/omnichat-model';

const DASHBOARD_PATH = `/parse/functions/dashboardLegacy`;

function hasDatePicker(type: OmniChatSchema.Feature) {
  return type !== Feature.DashboardAttendancePerTeams;
}

function getDashboardTitleByType(type: OmniChatSchema.Feature) {
  switch (type) {
    // Atendimento
    case Feature.DashboardAttendanceTime:
      return 'Tempos de atendimento';
    case Feature.DashboardAttendanceTimePerTeams:
      return 'Tempos de atendimento por time';
    case Feature.DashboardAttendancePerTeams:
      return 'Atendimento por time';
    case Feature.DashboardAttendance:
      return 'Atendimentos';
    case Feature.DashboardAttendanceTypePerTeams:
      return 'Atendimentos por time';
    case Feature.DashboardAttendanceUsers:
      return 'Usuários';
    case Feature.DashboardAttendanceTags:
      return 'Etiquetas';
    case Feature.DashboardAttendanceReasons:
      return 'Motivos dos atendimentos';

    // Atendimento por time
    case Feature.DashboardAttendanceTimePerUsers:
      return 'Tempos de atendimento por time';
    case Feature.DashboardAttendancePerUsers:
      return 'Atendimento por time';
    case Feature.DashboardAttendanceTeams:
      return 'Atendimentos';
    case Feature.DashboardAttendanceTypePerUsers:
      return 'Atendimentos por time';
    case Feature.DashboardAttendanceUsersPerTeams:
      return 'Usuários';
    case Feature.DashboardAttendanceTagsPerTeams:
      return 'Etiquetas';
    case Feature.DashboardAttendanceReasonsPerTeams:
      return 'Motivos dos atendimentos';

    // Vendas
    case Feature.DashboardSalesPerTeams:
      return 'Conversão de vendas';
    case Feature.DashboardSalesPerUsers:
      return 'Conversão de vendas';

    // Mensagem
    case Feature.DashboardWabPerTeams:
      return 'Whatsapp';
    case Feature.DashboardMessageChannels:
      return 'Mensagens por canal de atendimento';
    case Feature.DashboardFBMessengerPerTeams:
      return 'Facebook Messenger';
    case Feature.DashboardWebchatPerTeams:
      return 'Webchat';
    // Mensagem por time
    case Feature.DashboardFBMessengerPerUsers:
      return 'Facebook Messenger';
    case Feature.DashboardWabPerUsers:
      return 'Whatsapp';
    case Feature.DashboardWebchatPerUsers:
      return 'Webchat';
    default:
      return '';
  }
}

function getDashboardCardTypeByType(type: OmniChatSchema.Feature) {
  switch (type) {
    // Atendimento
    case Feature.DashboardAttendanceTime:
      return DashboardCardType.CHART;
    case Feature.DashboardAttendanceTimePerTeams:
      return DashboardCardType.TABLE;
    case Feature.DashboardAttendancePerTeams:
      return DashboardCardType.TABLE;
    case Feature.DashboardAttendance:
      return DashboardCardType.CHART;
    case Feature.DashboardAttendanceTypePerTeams:
      return DashboardCardType.TABLE;
    case Feature.DashboardAttendanceUsers:
      return DashboardCardType.TABLE_WITH_HEADER;
    case Feature.DashboardAttendanceTags:
      return DashboardCardType.TABLE;
    case Feature.DashboardAttendanceReasons:
      return DashboardCardType.TABLE;

    // Atendimento por time
    case Feature.DashboardAttendanceTimePerUsers:
      return DashboardCardType.TABLE;
    case Feature.DashboardAttendancePerUsers:
      return DashboardCardType.TABLE;
    case Feature.DashboardAttendanceTeams:
      return DashboardCardType.CHART;
    case Feature.DashboardAttendanceTypePerUsers:
      return DashboardCardType.TABLE;
    case Feature.DashboardAttendanceUsersPerTeams:
      return DashboardCardType.TABLE;
    case Feature.DashboardAttendanceTagsPerTeams:
      return DashboardCardType.TABLE;
    case Feature.DashboardAttendanceReasonsPerTeams:
      return DashboardCardType.TABLE;

    // Vendas
    case Feature.DashboardSalesPerTeams:
      return DashboardCardType.TABLE;
    case Feature.DashboardSalesPerUsers:
      return DashboardCardType.TABLE;

    // Mensagem
    case Feature.DashboardWabPerTeams:
      return DashboardCardType.TABLE;
    case Feature.DashboardMessageChannels:
      return DashboardCardType.CHART;
    case Feature.DashboardFBMessengerPerTeams:
      return DashboardCardType.TABLE;
    case Feature.DashboardWebchatPerTeams:
      return DashboardCardType.TABLE;
    // Mensagem por time
    case Feature.DashboardFBMessengerPerUsers:
      return DashboardCardType.TABLE;
    case Feature.DashboardWabPerUsers:
      return DashboardCardType.TABLE;
    case Feature.DashboardWebchatPerUsers:
      return DashboardCardType.TABLE;
    default:
      return DashboardCardType.CHART;
  }
}

async function treatDashboardResponse(
  body: { query: DashboardQuery },
  normalizeCB: (response: DashboardResponse, options?: any, granularity?: number) => DashboardDataNormalized,
  options?: NormalizeOptions,
) {
  const result = await ApiService.post(DASHBOARD_PATH, body);
  if (result.result.error) {
    throw new Error(result.result.error);
  } else {
    return normalizeCB(result.result.success, options, body.query.granularity);
  }
}

async function getDashboardDataByType(query: DashboardQuery): Promise<DashboardDataNormalized> {
  switch (query.type as any) {
    // Atendimento
    case Feature.DashboardAttendanceTime:
      return await treatDashboardResponse({ query }, normalizeChartResponse, {
        data: {
          unit: DashboardCardUnit.MINUTES,
        },
      });
    case Feature.DashboardAttendanceTimePerTeams:
      return await treatDashboardResponse({ query }, normalizeTableResponse, {
        data: { unit: DashboardCardUnit.MINUTES },
      });
    case Feature.DashboardAttendancePerTeams:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardAttendance:
      return await treatDashboardResponse({ query }, normalizeChartResponse);
    case Feature.DashboardAttendanceTypePerTeams:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardAttendanceUsers:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardAttendanceTags:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardAttendanceReasons:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardAttendanceTimePerUsers:
      return await treatDashboardResponse({ query }, normalizeTableResponse, {
        data: { unit: DashboardCardUnit.MINUTES },
      });
    case Feature.DashboardAttendancePerUsers:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardAttendanceTeams:
      return await treatDashboardResponse({ query }, normalizeChartResponse);
    case Feature.DashboardAttendanceTypePerUsers:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardAttendanceUsersPerTeams:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardAttendanceTagsPerTeams:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardAttendanceReasonsPerTeams:
      return await treatDashboardResponse({ query }, normalizeTableResponse);

    // Vendas
    case Feature.DashboardSalesPerTeams:
      return await treatDashboardResponse({ query }, normalizeTableResponse, {
        topHeader: [
          { unit: DashboardCardUnit.CURRENCY },
          { unit: DashboardCardUnit.CURRENCY },
          { unit: DashboardCardUnit.PERCENT },
        ],
      });
    case Feature.DashboardSalesPerUsers:
      return await treatDashboardResponse({ query }, normalizeTableResponse, {
        topHeader: [
          { unit: DashboardCardUnit.CURRENCY },
          { unit: DashboardCardUnit.CURRENCY },
          { unit: DashboardCardUnit.PERCENT },
        ],
      });

    // Mensagem
    case Feature.DashboardWabPerTeams:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardMessageChannels:
      return await treatDashboardResponse({ query }, normalizeChartResponse, {
        topHeader: [
          { icon: 'mdiWhatsapp', iconColor: 'green' },
          { icon: 'mdiFacebookMessenger', iconColor: 'blue' },
          { iconSrc: require('../assets/images/omnichat_logo.png') },
        ],
      });
    case Feature.DashboardFBMessengerPerTeams:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardFBMessengerPerUsers:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardWebchatPerTeams:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardWebchatPerUsers:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    case Feature.DashboardWabPerUsers:
      return await treatDashboardResponse({ query }, normalizeTableResponse);
    default:
      return {
        topHeader: [],
        dataColumns: [],
        data: [],
      };
  }
}

function normalizeTopHeader(responseTopHeader: DashboardCardData[], options: NormalizeOptions) {
  return (responseTopHeader || []).reduce((acc: any, value, index) => {
    const percent = (value as any).valuePast !== undefined && UtilsService.percent(
      (value as any).value[0],
      (value as any).valuePast[0] || 0,
    );
    return [
      ...acc,
      {
        ...value,
        unit: options && options.data && options && options.data.unit || DashboardCardUnit.NUMBER,
        ...options && options.topHeader && options.topHeader[index] || {},
        percent: percent && percent.toFixed(1),
        badgeColor: percent > 0 ? 'green' : 'red',
        badgeIcon: percent > 0 ? 'mdiArrowUp' : 'mdiArrowDown',
      },
    ];
  }, []);
}

/**
 * Receives a response data and returns the data normalized to the component DashboardCardChart
 *
 * @params DashboardResponse, DashboardCardUnit
 *
 * @returns DashboardDataNormalized
 * @example
 * {
 *   topHeader: [],
 *   dataColumns: ['Time', 'TMA'],
 *   data: [{ Team: {objectId...}, TMA: {value: x, valuePast: x} }]
 * }
 */
function normalizeChartResponse(
    response: DashboardResponse,
    options: NormalizeOptions,
    granularity: number = 10,
  ): DashboardDataNormalized {
  const topHeaderNormalized: DashboardCardData[] = normalizeTopHeader(response.topHeader || [], options);
  const dataNormalized: DashboardCardData[] = (response.data || []).map((data) => (
    data.reduce((acc: any, value) => ([
      ...acc,
      {
        ...value,
        unit: options && options.data && options && options.data.unit || DashboardCardUnit.NUMBER,
        label: UtilsService.granularityFormat(granularity, (value.label as number)),
      },
    ]), [])
  ));
  return {
    topHeader: topHeaderNormalized,
    dataColumns: response.dataColumns,
    data: dataNormalized,
  };
}

/**
 * Receives a response data and returns the data normalized to the component DashboardCardTable
 *
 * @params DashboardResponse
 *
 * @returns DashboardDataNormalized
 * @example
 * {
 *   topHeader: [],
 *   dataColumns: ['Time', 'TMA'],
 *   data: [{ Team: {objectId...}, TMA: {value: x, valuePast: x} }]
 * }
 */
function normalizeTableResponse(response: DashboardResponse, options: NormalizeOptions): DashboardDataNormalized {
  const topHeaderNormalized: DashboardCardData[] = normalizeTopHeader(response.topHeader || [], options);
  const dataNormalized: DashboardCardData[] = (response.data || [])[0].map((data: any) => {
    const isTotalRow = data.label === 'Total';
    const isAverageRow = data.label === 'Média geral';
    const newObj: any = {};
    if (data.label.className) {
      newObj[data.label.className] = data.label;
    } else {
      newObj.label = {
        textClass: 'f5 ' + ((isTotalRow || isAverageRow) && 'b' || ''),
        value: data.label,
      };
    }
    for (let i = 1; i < response.dataColumns.length; i++) {
      let percent = 0;
      if (data.valuePast !== undefined) {
        percent = UtilsService.percent(Number(data.value[i - 1]), Number(data.valuePast[i - 1]));
      }

      newObj[response.dataColumns[i] as string] = {
        value: typeof data.value === 'string' ? data.value : data.value[i - 1],
        ...data.valuePast && { valuePast: data.valuePast[i - 1] },
        ...(isTotalRow || isAverageRow) && { textClass: 'b' },
        percent,
        unit: options && options.data && options.data.unit || DashboardCardUnit.NUMBER,
      };
    }
    return newObj;
  });
  return {
    topHeader: topHeaderNormalized,
    dataColumns: response.dataColumns,
    data: dataNormalized,
    totalCount: response.count,
  };
}

export const DashboardService = {
  getDashboardTitleByType,
  getDashboardCardTypeByType,
  getDashboardDataByType,
  hasDatePicker,
};
